// EPSE Quarter Data Hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EPSEQuarterData, TimeContext } from '@/types/epse';
import { labelTimeContext } from '@/lib/epseTimeContext';
import { toast } from 'sonner';

// Paginagrootte voor het uitlezen van kwartierdata. PostgREST levert nooit meer dan zijn eigen
// db-max-rows (standaard 1000 op Supabase), dus hoger vragen heeft geen zin.
const QUARTER_PAGE_SIZE = 1000;

// Harde bovengrens tegen een eindeloze lus als de server zich onverwacht gedraagt.
// 500.000 kwartieren is ruim 14 jaar aan data; de grootste site telt er vandaag 35.175.
// Bewust een zichtbare fout in plaats van stil afkappen — dat laatste is precies de bug hieronder.
const MAX_QUARTER_ROWS = 500_000;

export function useEPSEQuarterData(siteId: string | undefined) {
  const queryClient = useQueryClient();
  
  const { data: quarterData, isLoading, error } = useQuery({
    queryKey: ['epse-quarter-data', siteId],
    queryFn: async () => {
      if (!siteId) return [];

      // WAAROM een paginatielus: deze select stond zonder .range() en zonder .limit(), en
      // PostgREST kapt zo'n query stil af op db-max-rows (standaard 1000). Gemeten in
      // docs/onderzoek/t42/epse.md §4: site "Referentie" (49765fc1-…) heeft 35.175 kwartieren, maar de
      // opgeslagen simulatie 2ff2c413-… reproduceert bit-voor-bit op alléén de eerste 1000 rijen
      // (15808.459868421054 import / 633 overschrijdingen / 202.07624999999996 kWh doorzet).
      // Dat is 2023-12-31 23:00 t/m 2024-01-11 08:45 — 10,4 dagen van 366, oftewel 2,8% van het
      // jaar — gepresenteerd als jaarcijfer met status 'completed' en zonder enige waarschuwing.
      //
      // Kan de aanroeper de volledige reeks aan? Nagemeten met een Node-script op exact de
      // EPSEQuarterData-vorm (src/types/epse.ts:86-96, inclusief time_context):
      //   35.175 rijen = 11,1 MB heap (332 byte/rij), 15,0 MB JSON over de lijn,
      //   37 requests van ~0,43 MB.
      // Dat past ruim in een desktopbrowser en React Query cachet het per site; met alle drie de
      // sites tegelijk in de cache is het ~33 MB. Acceptabel. De echte kostenpost zit buiten dit
      // bestand: epseSimulationEngine schrijft per kwartier een SoC-punt plus vier reeksen naar
      // epse_simulations.results (JSONB), en die kolom groeit gemeten van 0,20 MB (afgekapte 1000
      // kwartieren) naar 7,25 MB voor een heel jaar — factor 37,1. Zie het risico in de overdracht.
      const rows: EPSEQuarterData[] = [];
      let offset = 0;

      for (;;) {
        const { data, error } = await supabase
          .from('epse_quarter_data')
          .select('*')
          .eq('site_id', siteId)
          // start_time is GEEN unieke sleutel: "Referentie" heeft 35.175 rijen op 35.131 distincte
          // tijdstempels (44 duplicaten, rapport §4/§6). Sorteren op alleen start_time laat de
          // volgorde van die duplicaten aan Postgres over, en dat mag per query verschillen —
          // waardoor precies op een paginagrens een rij dubbel of helemaal niet meekomt. De
          // id-tiebreak maakt de sortering totaal en dus stabiel over alle 37 pagina's heen.
          .order('start_time', { ascending: true })
          .order('id', { ascending: true })
          .range(offset, offset + QUARTER_PAGE_SIZE - 1);

        if (error) throw error;

        const batch = (data ?? []) as unknown as EPSEQuarterData[];
        if (batch.length === 0) break;

        rows.push(...batch);
        // Opschuiven met wat de server WERKELIJK teruggaf, niet met QUARTER_PAGE_SIZE: staat
        // db-max-rows lager dan 1000, dan zou een vaste stap rijen overslaan — dezelfde stille
        // afkapping in een nieuw jasje. Gevolg is één extra, lege request aan het eind (37 i.p.v. 36).
        offset += batch.length;

        if (rows.length > MAX_QUARTER_ROWS) {
          throw new Error(
            `Kwartierdata voor deze site overschrijdt ${MAX_QUARTER_ROWS.toLocaleString('nl-NL')} rijen; ` +
            `inlezen afgebroken. Controleer de dataset voordat je hierop rekent.`
          );
        }
      }

      return rows;
    },
    enabled: !!siteId,
  });
  
  const uploadQuarterData = useMutation({
    mutationFn: async ({
      siteId,
      data,
      schedule,
    }: {
      siteId: string;
      data: Omit<EPSEQuarterData, 'id' | 'site_id' | 'created_at'>[];
      schedule?: import('@/types/epse').OperatingSchedule;
    }) => {
      // Delete existing data for this site
      await supabase
        .from('epse_quarter_data')
        .delete()
        .eq('site_id', siteId);
      
      // Process data with time context
      const processedData = data.map(q => ({
        site_id: siteId,
        start_time: q.start_time,
        end_time: q.end_time,
        kw_value: q.kw_value,
        value_type: q.value_type,
        pv_kw: q.pv_kw,
        time_context: labelTimeContext(q.start_time, schedule) as any,
      }));
      
      // Insert in batches of 500
      const batchSize = 500;
      for (let i = 0; i < processedData.length; i += batchSize) {
        const batch = processedData.slice(i, i + batchSize);
        const { error } = await supabase
          .from('epse_quarter_data')
          .insert(batch);
        
        if (error) throw error;
      }
      
      return processedData.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ['epse-quarter-data'] });
      toast.success(`${count.toLocaleString('nl-NL')} kwartierwaarden geüpload`);
    },
    onError: (error) => {
      toast.error(`Fout bij uploaden: ${error.message}`);
    },
  });
  
  const deleteQuarterData = useMutation({
    mutationFn: async (siteId: string) => {
      const { error } = await supabase
        .from('epse_quarter_data')
        .delete()
        .eq('site_id', siteId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epse-quarter-data'] });
      toast.success('Kwartierdata verwijderd');
    },
    onError: (error) => {
      toast.error(`Fout bij verwijderen: ${error.message}`);
    },
  });
  
  return {
    quarterData,
    isLoading,
    error,
    uploadQuarterData,
    deleteQuarterData,
  };
}
